import type { components } from '@/generated/api/schema';

/** Shared parameter shape used by the document-list query key. */
interface DateiablageListKeyParams {
  readonly page: number;
  readonly size: number;
  readonly sort: string;
  readonly search?: string | undefined;
  readonly kategorie?: components['schemas']['DokumentKategorie'] | undefined;
  readonly hochgeladenAmFrom?: string | undefined;
  readonly hochgeladenAmTo?: string | undefined;
  readonly korrespondentId?: string | undefined;
  readonly versenderId?: string | undefined;
  readonly empfaengerId?: string | undefined;
}

/** Parameter shape used by the correspondent-history query key — mirrors its query params. */
export interface AustauschpartnerKeyParams {
  readonly search?: string | undefined;
  readonly selectedId?: string | undefined;
  readonly richtung?: components['schemas']['VersandRichtung'] | undefined;
}

/** Parameter shape used by the recipient-candidate query key — mirrors its query params. */
export interface EmpfaengerKandidatenKeyParams {
  readonly search?: string | undefined;
  readonly gruppe?: components['schemas']['EmpfaengerGruppe'] | undefined;
  readonly dokumentId?: string | undefined;
}

/**
 * TanStack Query key builders for the `dateiablage` feature.
 *
 * There is exactly one document list — unlike the customers feature there is no per-audience
 * split, since the server derives visibility (and `richtung`) from the caller's own access-list
 * membership. `austauschpartnerAll`/`austauschpartner` back the list filter's value range;
 * `empfaengerKandidatenAll`/`empfaengerKandidaten` back the upload and share dialogs' recipient
 * picker — two deliberately separate concepts (current relationship set vs. access-list history,
 * see `useEmpfaengerKandidaten`/`useAustauschpartner`) that both live in this shared file
 * rather than a route-local one.
 */
export const dateiablageKeys = {
  /** Root key for all dateiablage-feature queries — use to invalidate the entire feature cache. */
  all: ['dateiablage'] as const,

  /** All document-list queries. */
  listAll: () => [...dateiablageKeys.all, 'list'] as const,

  /** Document-list query keyed by pagination and filter params. */
  list: (params: DateiablageListKeyParams) => [...dateiablageKeys.listAll(), params] as const,

  /**
   * Unread-documents counter query (`GET /api/v1/dateiablage/ungelesen-zaehler`) — a single,
   * unparameterized entry scoped entirely by the caller's own organization via the app token.
   */
  ungelesenZaehler: () => [...dateiablageKeys.all, 'ungelesen-zaehler'] as const,

  /** All correspondent-history queries, regardless of search term / selected id. */
  austauschpartnerAll: () => [...dateiablageKeys.all, 'austauschpartner'] as const,

  /**
   * Correspondent-history query (`GET /api/v1/dateiablage/austauschpartner`), keyed by the
   * server-side search term, the deep-linked `selectedId`, and the Support-view `richtung` filter —
   * the result depends on all three, on top of the caller's own access-list history, which is
   * derived server-side and therefore not part of the key.
   */
  austauschpartner: (params: AustauschpartnerKeyParams = {}) =>
    [...dateiablageKeys.austauschpartnerAll(), params] as const,

  /** All recipient-candidate queries, regardless of search term / group / share-context document. */
  empfaengerKandidatenAll: () => [...dateiablageKeys.all, 'empfaenger-kandidaten'] as const,

  /**
   * Recipient-candidate query (`GET /api/v1/dateiablage/empfaenger-kandidaten`), keyed by the
   * server-side search term, the `gruppe` restriction, and the share-context `dokumentId` — the
   * result depends on all three, on top of the caller's own current relationship set, which is
   * derived server-side and therefore not part of the key.
   */
  empfaengerKandidaten: (params: EmpfaengerKandidatenKeyParams = {}) =>
    [...dateiablageKeys.empfaengerKandidatenAll(), params] as const,

  /**
   * Query keys to invalidate after a successful `teilen` (share) mutation — the document list
   * (the sharer's own row is untouched, but other filters/pages may now surface the document
   * differently), the correspondent value list, and the recipient-candidate list, since the newly
   * authorized recipient becomes one of the sharer's correspondents immediately (its access-list
   * entry now exists on a document already visible to the sharer) and a share-context candidate
   * query for this document must no longer offer it.
   */
  teilenInvalidation: () =>
    [
      dateiablageKeys.listAll(),
      dateiablageKeys.austauschpartnerAll(),
      dateiablageKeys.empfaengerKandidatenAll(),
    ] as const,
};
